import { Media } from "@omni/types/api/shared/bff/media.model";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, Dispatch, ReactNode, SetStateAction, use } from "react";
import SwiperCore from "swiper";

type ImageGalleryContextValue = {
    images: Array<Media>;
    activeIndex: number;
    mainSwiper?: SwiperCore;
    thumbSwiper?: SwiperCore;
    setActiveIndex: (action: (prevState: number) => number) => void;
    next: () => void;
    previous: () => void;
    setMainSwiper: Dispatch<SetStateAction<SwiperCore | undefined>>;
    setThumbSwiper: Dispatch<SetStateAction<SwiperCore | undefined>>;
};

type ImageGalleryContextProviderProps = {
    images: Array<Media>;
    activeIndex: number;
    mainSwiper?: SwiperCore;
    thumbSwiper?: SwiperCore;
    setActiveIndex: (action: (prevState: number) => number) => void;
    next: () => void;
    previous: () => void;
    setMainSwiper: Dispatch<SetStateAction<SwiperCore | undefined>>;
    setThumbSwiper: Dispatch<SetStateAction<SwiperCore | undefined>>;
    children: ReactNode;
};

export const ImageGalleryContext = createContext<
    ImageGalleryContextValue | undefined
>(undefined);
ImageGalleryContext.displayName = "ImageGalleryContext";

export const ImageGalleryContextProvider = ({
    images,
    activeIndex,
    mainSwiper,
    thumbSwiper,
    setActiveIndex,
    next,
    previous,
    setMainSwiper,
    setThumbSwiper,
    children
}: ImageGalleryContextProviderProps) => {
    return (
        <ImageGalleryContext
            value={{
                images: images,
                activeIndex: activeIndex,
                mainSwiper: mainSwiper,
                thumbSwiper: thumbSwiper,
                setActiveIndex: setActiveIndex,
                next: next,
                previous: previous,
                setMainSwiper: setMainSwiper,
                setThumbSwiper: setThumbSwiper
            }}
        >
            {children}
        </ImageGalleryContext>
    );
};

export const useImageGalleryContext = () => {
    const context = use<ImageGalleryContextValue | undefined>(
        ImageGalleryContext
    );

    if (!isDefined(context)) {
        throw new Error(`${ImageGalleryContext.displayName} is undefined.`);
    }

    return context;
};
